import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import api from '../../utils/api';
import { Counsellor } from '../../types/counsellor';

// Extended referee type with additional fields for reference tracking
interface RefereeWithTracking {
  name: string;
  email: string;
  phone: string;
  mobile: string;
  relationship: string;
  yearsKnown: string;
  address: string;
  jobTitle: string;
  company: string;
  reference?: {
    url: string;
    fileName: string;
    content?: string;
  };
  // Backend properties for reference tracking
  referenceRequestSent?: boolean;
  referenceRequestSentAt?: Date;
  referenceReceivedAt?: Date;
  referenceToken?: string;
  // Additional properties from backend
  referenceEmailSent?: boolean;
  referenceEmailSentDate?: Date;
  referenceSubmitted?: boolean;
  referenceSubmittedDate?: Date;
  referenceContent?: any; // For form-submitted references
  referenceTokenExpiryDate?: Date;
  // Delivery tracking fields
  brevoMessageId?: string;
  deliveryStatus?: 'pending' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'invalid' | 'completed' | 'expired';
  deliveryEvents?: Array<{
    event: string;
    timestamp: Date;
    brevoEventData?: any;
  }>;
  lastDeliveryEvent?: Date;
  bounceReason?: string;
  resendCount?: number;
  lastResendDate?: Date;
}

interface ReferenceManagementProps {
  counsellorId: string;
  counsellorData: Counsellor;
  isAdmin?: boolean;
  onRefresh: () => void;
}

const ReferenceManagement: React.FC<ReferenceManagementProps> = ({
  counsellorId,
  counsellorData,
  isAdmin = false,
  onRefresh
}) => {
  // State variables
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [resendReferenceDialogOpen, setResendReferenceDialogOpen] = useState(false);
  const [viewReferenceDialogOpen, setViewReferenceDialogOpen] = useState(false);
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  const [selectedRefereeType, setSelectedRefereeType] = useState<'firstReferee' | 'secondReferee' | null>(null);
  const [selectedReference, setSelectedReference] = useState<any>(null);

  // Check if interview has been accepted - only then should we show reference management
  const interviewAccepted = [
    'Interview Completed',
    'Legal Information Requested',
    'Legal Information Submitted',
    'Legal Info Requested',
    'Legal Info Submitted',
    'Legal Info Update Requested',
    'Legal Info Approved',
    'Legal Info Rejected',
    'Onboarding Review',
    'Ready For Onboarding',
    'Pending Agreement',
    'Accepted',
  ].includes(counsellorData.applicationStatus ?? '');

  // Get referee data and cast to our extended type
  const firstReferee = counsellorData.application?.referee?.firstReferee as RefereeWithTracking | undefined;
  const secondReferee = counsellorData.application?.referee?.secondReferee as RefereeWithTracking | undefined;

  // Handle resending reference request
  const handleResendReferenceRequest = async () => {
    if (!selectedRefereeType) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await api.post(`/api/counsellors/${counsellorId}/resend-reference-request`, {
        refereeType: selectedRefereeType
      });
      
      setSuccess(`Reference request resent to ${selectedRefereeType === 'firstReferee' ? firstReferee?.name : secondReferee?.name} successfully!`);
      setResendReferenceDialogOpen(false);
      onRefresh();
    } catch (error: any) {
      console.error('Error resending reference request:', error);
      setError(error.response?.data?.error || 'Failed to resend reference request');
    } finally {
      setLoading(false);
    }
  };

  // Handle refreshing expired references (admin only)
  const handleRefreshReferences = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post(`/api/referee/counsellor/${counsellorId}/refresh-references`);
      
      const refreshedCount = response.data.refreshedReferences?.length || 0;
      if (refreshedCount > 0) {
        setSuccess(`Successfully refreshed ${refreshedCount} reference request(s). New emails sent with extended expiry dates.`);
      } else {
        setSuccess('No expired references found to refresh.');
      }
      
      setRefreshDialogOpen(false);
      onRefresh();
    } catch (error: any) {
      console.error('Error refreshing references:', error);
      setError(error.response?.data?.message || 'Failed to refresh references');
    } finally {
      setLoading(false);
    }
  };

  // Check if any references need refreshing (expired tokens)
  const hasExpiredReferences = () => {
    const now = new Date();
    const firstExpired = firstReferee && !firstReferee.referenceSubmitted && 
      firstReferee.referenceTokenExpiryDate && new Date(firstReferee.referenceTokenExpiryDate) < now;
    const secondExpired = secondReferee && !secondReferee.referenceSubmitted && 
      secondReferee.referenceTokenExpiryDate && new Date(secondReferee.referenceTokenExpiryDate) < now;
    return firstExpired || secondExpired;
  };

  // Helper function to get reference status chip with delivery tracking
  const getReferenceStatusChip = (referee: any) => {
    if (!referee) return <Chip label="Not Provided" color="default" size="small" />;
    
    // Priority 1: Check delivery status first (most accurate)
    if (referee.deliveryStatus) {
      switch (referee.deliveryStatus) {
        case 'completed':
          return <Chip 
            icon={<CheckCircleIcon />} 
            label="Reference Received" 
            color="success" 
            size="small" 
          />;
        case 'bounced':
          return <Tooltip title={referee.bounceReason || 'Email bounced'}>
            <Chip 
              icon={<CancelIcon />} 
              label="Email Bounced" 
              sx={{ backgroundColor: '#fee2e2', color: '#991b1b' }}
              size="small" 
            />
          </Tooltip>;
        case 'invalid':
          return <Chip 
            icon={<CancelIcon />} 
            label="Invalid Email" 
            sx={{ backgroundColor: '#fee2e2', color: '#991b1b' }}
            size="small" 
          />;
        case 'clicked':
          return <Chip 
            label="Link Clicked" 
            sx={{ backgroundColor: '#dbeafe', color: '#1e40af' }}
            size="small" 
          />;
        case 'opened':
          return <Chip 
            label="Email Opened" 
            sx={{ backgroundColor: '#e0e7ff', color: '#3730a3' }}
            size="small" 
          />;
        case 'delivered':
          return <Chip 
            label="Email Delivered" 
            color="info" 
            size="small" 
          />;
        case 'pending':
          return <Chip 
            label="Sending..." 
            color="warning" 
            size="small" 
          />;
        case 'expired':
          return <Chip 
            label="Link Expired" 
            sx={{ backgroundColor: '#fed7aa', color: '#9a3412' }}
            size="small" 
          />;
      }
    }
    
    // Priority 2: Check for reference submission (legacy)
    if (referee.referenceSubmitted === true || referee.referenceContent || referee.reference) {
      return <Chip 
        icon={<CheckCircleIcon />} 
        label="Reference Received" 
        color="success" 
        size="small" 
      />;
    }
    
    // Priority 3: Check for email sent (legacy)
    if (referee.referenceEmailSent || referee.referenceRequestSent) {
      return <Chip 
        label="Request Sent" 
        color="primary" 
        size="small" 
      />;
    }
    
    // Default: Not submitted
    return <Chip 
      icon={<CancelIcon />} 
      label="Not Submitted" 
      color="warning" 
      size="small" 
    />;
  };

  // Helper function to format date
  const formatDate = (date: string | Date | undefined) => {
    if (!date) return 'Not set';
    return dayjs(date).format('DD/MM/YYYY HH:mm');
  };

  // Only render if interview has been accepted or if admin
  if (!interviewAccepted && !isAdmin) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Reference Management
            </Typography>
            {isAdmin && (firstReferee || secondReferee) && (
              <Tooltip title="Refresh expired reference requests and send new emails with extended expiry dates">
                <Button
                  startIcon={<RefreshIcon />}
                  size="small"
                  variant="outlined"
                  color={hasExpiredReferences() ? 'warning' : 'primary'}
                  disabled={loading}
                  onClick={() => setRefreshDialogOpen(true)}
                >
                  Refresh References
                </Button>
              </Tooltip>
            )}
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            {/* First Referee */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">Professional Referee</Typography>
                    {getReferenceStatusChip(firstReferee)}
                  </Box>
                  
                  {firstReferee ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Name</Typography>
                        <Typography>{firstReferee.name || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Email</Typography>
                        <Typography>{firstReferee.email || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Phone</Typography>
                        <Typography>{firstReferee.phone || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Mobile</Typography>
                        <Typography>{firstReferee.mobile || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Relationship</Typography>
                        <Typography>{firstReferee.relationship || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Years Known</Typography>
                        <Typography>{firstReferee.yearsKnown || 'Not provided'}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Occupation</Typography>
                        <Typography>{firstReferee.jobTitle || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Company</Typography>
                        <Typography>{firstReferee.company || 'Not provided'}</Typography>
                      </Grid>
                      {firstReferee.referenceRequestSent && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" fontWeight="bold">Request Sent</Typography>
                          <Typography>{formatDate(firstReferee.referenceRequestSentAt)}</Typography>
                        </Grid>
                      )}
                      {firstReferee.referenceTokenExpiryDate && !firstReferee.referenceSubmitted && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" fontWeight="bold">Link Expires</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>
                              {formatDate(firstReferee.referenceTokenExpiryDate)}
                            </Typography>
                            {new Date(firstReferee.referenceTokenExpiryDate) < new Date() && (
                              <Chip
                                icon={<WarningIcon />}
                                label="Expired"
                                size="small"
                                sx={{ backgroundColor: '#fed7aa', color: '#9a3412' }}
                              />
                            )}
                          </Box>
                        </Grid>
                      )}
                      {firstReferee.reference && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" fontWeight="bold">Reference Received</Typography>
                          <Typography>{formatDate(firstReferee.referenceReceivedAt)}</Typography>
                        </Grid>
                      )}
                      
                      {/* Delivery tracking info for admins */}
                      {isAdmin && firstReferee.deliveryEvents && firstReferee.deliveryEvents.length > 0 && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Delivery Timeline
                          </Typography>
                          <List dense>
                            {firstReferee.deliveryEvents.map((event: any, index: number) => (
                              <ListItem key={index} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={event.event}
                                  secondary={formatDate(event.timestamp)}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                          {firstReferee.resendCount && firstReferee.resendCount > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Resent {firstReferee.resendCount} time{firstReferee.resendCount > 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No referee information provided
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  {/* For admins: Show View Reference button if reference has been received, otherwise show nothing */}
                  {isAdmin && firstReferee && (
                    <>
                      {/* View Reference button - only visible to admins when reference has been received */}
                      {(firstReferee.reference || firstReferee.referenceContent) ? (
                        <Button
                          startIcon={<VisibilityIcon />}
                          size="small"
                          onClick={() => {
                            // Create a reference object that includes all possible reference data
                            const referenceData = {
                              ...firstReferee.reference,
                              content: firstReferee.referenceContent ? JSON.stringify(firstReferee.referenceContent, null, 2) : (firstReferee.reference?.content || null),
                              url: firstReferee.reference?.url || null,
                              fileName: firstReferee.reference?.fileName || null
                            };
                            setSelectedReference(referenceData);
                            setViewReferenceDialogOpen(true);
                          }}
                        >
                          View Reference
                        </Button>
                      ) : null /* No button for admins if reference has not been received */}
                    </>
                  )}
                  
                  {/* For counsellors: Only show Resend Request button if reference is not submitted */}
                  {!isAdmin && firstReferee && firstReferee.email && 
                   !(firstReferee.referenceSubmitted === true || firstReferee.referenceContent || firstReferee.reference) && (
                    <Button
                      startIcon={<EmailIcon />}
                      size="small"
                      disabled={loading}
                      onClick={() => {
                        setSelectedRefereeType('firstReferee');
                        setResendReferenceDialogOpen(true);
                      }}
                    >
                      Resend Request
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
            
            {/* Second Referee */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">Personal Referee</Typography>
                    {getReferenceStatusChip(secondReferee)}
                  </Box>
                  
                  {secondReferee ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Name</Typography>
                        <Typography>{secondReferee.name || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Email</Typography>
                        <Typography>{secondReferee.email || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Phone</Typography>
                        <Typography>{secondReferee.phone || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Mobile</Typography>
                        <Typography>{secondReferee.mobile || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Relationship</Typography>
                        <Typography>{secondReferee.relationship || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Years Known</Typography>
                        <Typography>{secondReferee.yearsKnown || 'Not provided'}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Occupation</Typography>
                        <Typography>{secondReferee.jobTitle || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold">Organisation</Typography>
                        <Typography>{secondReferee.company || 'Not provided'}</Typography>
                      </Grid>
                      {secondReferee.referenceRequestSent && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" fontWeight="bold">Request Sent</Typography>
                          <Typography>{formatDate(secondReferee.referenceRequestSentAt)}</Typography>
                        </Grid>
                      )}
                      {secondReferee.referenceTokenExpiryDate && !secondReferee.referenceSubmitted && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" fontWeight="bold">Link Expires</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>
                              {formatDate(secondReferee.referenceTokenExpiryDate)}
                            </Typography>
                            {new Date(secondReferee.referenceTokenExpiryDate) < new Date() && (
                              <Chip
                                icon={<WarningIcon />}
                                label="Expired"
                                size="small"
                                sx={{ backgroundColor: '#fed7aa', color: '#9a3412' }}
                              />
                            )}
                          </Box>
                        </Grid>
                      )}
                      {secondReferee.reference && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" fontWeight="bold">Reference Received</Typography>
                          <Typography>{formatDate(secondReferee.referenceReceivedAt)}</Typography>
                        </Grid>
                      )}
                      
                      {/* Delivery tracking info for admins */}
                      {isAdmin && secondReferee.deliveryEvents && secondReferee.deliveryEvents.length > 0 && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Delivery Timeline
                          </Typography>
                          <List dense>
                            {secondReferee.deliveryEvents.map((event: any, index: number) => (
                              <ListItem key={index} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={event.event}
                                  secondary={formatDate(event.timestamp)}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                          {secondReferee.resendCount && secondReferee.resendCount > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Resent {secondReferee.resendCount} time{secondReferee.resendCount > 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No referee information provided
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  {/* For admins: Show View Reference button if reference has been received, otherwise show nothing */}
                  {isAdmin && secondReferee && (
                    <>
                      {/* View Reference button - only visible to admins when reference has been received */}
                      {(secondReferee.reference || secondReferee.referenceContent) ? (
                        <Button
                          startIcon={<VisibilityIcon />}
                          size="small"
                          onClick={() => {
                            // Create a reference object that includes all possible reference data
                            const referenceData = {
                              ...secondReferee.reference,
                              content: secondReferee.referenceContent ? JSON.stringify(secondReferee.referenceContent, null, 2) : (secondReferee.reference?.content || null),
                              url: secondReferee.reference?.url || null,
                              fileName: secondReferee.reference?.fileName || null
                            };
                            setSelectedReference(referenceData);
                            setViewReferenceDialogOpen(true);
                          }}
                        >
                          View Reference
                        </Button>
                      ) : null /* No button for admins if reference has not been received */}
                    </>
                  )}
                  
                  {/* For counsellors: Only show Resend Request button if reference is not submitted */}
                  {!isAdmin && secondReferee && secondReferee.email && 
                   !(secondReferee.referenceSubmitted === true || secondReferee.referenceContent || secondReferee.reference) && (
                    <Button
                      startIcon={<EmailIcon />}
                      size="small"
                      disabled={loading}
                      onClick={() => {
                        setSelectedRefereeType('secondReferee');
                        setResendReferenceDialogOpen(true);
                      }}
                    >
                      Resend Request
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          </Grid>
          
          {/* Send All Reference Requests button removed as references are now sent automatically */}
        </CardContent>
      </Card>
      
      {/* Refresh References Dialog (Admin Only) */}
      <Dialog open={refreshDialogOpen} onClose={() => setRefreshDialogOpen(false)}>
        <DialogTitle>Refresh Reference Requests</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            This will check for any outstanding reference requests with expired tokens and:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Generate new reference tokens with fresh 28-day expiry dates" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Send new reference request emails to referees" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Only affect references that haven't been submitted yet" />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            {hasExpiredReferences() 
              ? 'Expired references detected. Refreshing will reissue these requests.'
              : 'No expired references found, but you can still refresh to extend expiry dates.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefreshDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleRefreshReferences}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {loading ? 'Refreshing...' : 'Refresh References'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Reference Request Dialog */}
      <Dialog open={resendReferenceDialogOpen} onClose={() => setResendReferenceDialogOpen(false)}>
        <DialogTitle>
          {selectedRefereeType === 'firstReferee' 
            ? `Resend Reference Request to ${firstReferee?.name}`
            : `Resend Reference Request to ${secondReferee?.name}`}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to resend the reference request email to{' '}
            {selectedRefereeType === 'firstReferee' ? firstReferee?.email : secondReferee?.email}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendReferenceDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleResendReferenceRequest}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Resend Request'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* View Reference Dialog */}
      <Dialog 
        open={viewReferenceDialogOpen} 
        onClose={() => setViewReferenceDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Reference Details</DialogTitle>
        <DialogContent>
          {selectedReference && (
            <Box>
              {selectedReference.content ? (
                <Box>
                  {/* Check if content is JSON string (from referenceContent object) */}
                  {typeof selectedReference.content === 'string' && selectedReference.content.startsWith('{') ? (
                    <Box>
                      {(() => {
                        try {
                          const referenceData = JSON.parse(selectedReference.content);
                          return (
                            <List>
                              {referenceData.howLongKnown && (
                                <ListItem>
                                  <ListItemText 
                                    primary="How long have you known the applicant?" 
                                    secondary={referenceData.howLongKnown} 
                                  />
                                </ListItem>
                              )}
                              {referenceData.capacityKnown && (
                                <ListItem>
                                  <ListItemText 
                                    primary="In what capacity do you know the applicant?" 
                                    secondary={referenceData.capacityKnown} 
                                  />
                                </ListItem>
                              )}
                              {referenceData.suitability && (
                                <ListItem>
                                  <ListItemText 
                                    primary="How would you rate their suitability as a counsellor?" 
                                    secondary={referenceData.suitability} 
                                  />
                                </ListItem>
                              )}
                              {referenceData.concerns && (
                                <ListItem>
                                  <ListItemText 
                                    primary="Do you have any concerns about their suitability?" 
                                    secondary={referenceData.concerns} 
                                  />
                                </ListItem>
                              )}
                              {referenceData.additionalComments && (
                                <ListItem>
                                  <ListItemText 
                                    primary="Additional Comments" 
                                    secondary={referenceData.additionalComments} 
                                  />
                                </ListItem>
                              )}
                            </List>
                          );
                        } catch (e) {
                          // If parsing fails, just show the content as is
                          return (
                            <Typography variant="body1" whiteSpace="pre-wrap">
                              {selectedReference.content}
                            </Typography>
                          );
                        }
                      })()}
                    </Box>
                  ) : (
                    <Typography variant="body1" whiteSpace="pre-wrap">
                      {selectedReference.content}
                    </Typography>
                  )}
                </Box>
              ) : selectedReference.url ? (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    Reference document:
                  </Typography>
                  <Button
                    variant="outlined"
                    href={selectedReference.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Document ({selectedReference.fileName})
                  </Button>
                </Box>
              ) : (
                <Typography>No reference content available</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewReferenceDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReferenceManagement;
